import React, {useState} from "react";
import { useFormik } from "formik";
import valid from "card-validator";
import {useNavigate} from "react-router-dom";
import { useSetRecoilState, useRecoilValue, useRecoilState } from "recoil";
import StyledSelect from "@shared/components/Select";
import Input from "@shared/components/Input";
import HeartLoader from "@shared/HeartLoader";
import { secondaryModals, secondOverlayState, giftCheckoutState } from '@atoms';
import "./style.css";
import {paymentErrorsConfig} from "../../../helpers/PaymentErrors";
import useCardOrderPayment, { getCardExpDate } from "@helpers/hooks/useCardOrderPayment";
import {payGiftCardWithCard} from "../../../api/giftcard";

const AddCardForm = () => {
  const [cardErr, setCardErr] = useState({
    cardNumberErr: false,
    cardHolderErr: false,
    cardExpMonErr: false,
    cardExpYearErr: false,
    cardCVVErr: false,
    cardIdErr: false,
  });
  const [isLoading, setLoading] = useState(false);
  const setModals = useSetRecoilState(secondaryModals);
  const [, setOverlay] = useRecoilState(secondOverlayState);

  // GIFT-CARD mode: one synchronous full-amount charge (regular J7 debit)
  // against the pre-created GC- intent — no card-check leg and no drawer
  // finalize step. The server charges its OWN computed amount.
  const giftCheckout = useRecoilValue(giftCheckoutState);
  const giftMode = !!giftCheckout;

  const navigate = useNavigate();

  // PHOTO orders: one press = J2 card check -> charge (only on 000) -> order
  // recorded -> success page. No second "אישור הזמנה" press in the drawer.
  const { payAndOrder, cancelPending, isPaying, paymentError: orderPaymentError } = useCardOrderPayment();
  const [paymentError, setPaymentError] = useState("");

  const getCurrentMonthString = () => {
    const month = new Date().getMonth()+1;
    if (month > 10) return `${month}`;
    return `0${month}`;
  }

  // Gift card: the server charges the intent's amount in one go and activates
  // the card synchronously — success routes straight to /gift-success.
  const handleGiftCardPayment = async (values) => {
    try {
      setLoading(true);
      setPaymentError("");
      const res = await payGiftCardWithCard({
        giftOrderId: giftCheckout.giftOrderId,
        card: {
          no: values.cardNumber.replace(/\s/g, ''),
          expdate: getCardExpDate(values.expiriedMonth, values.expiriedYear),
          cvv: values.cvv,
          id: values.id,
          email: giftCheckout.buyerEmail || "",
        },
      });

      if (!res.success) {
        if (res.errorCode in paymentErrorsConfig) {
          setPaymentError(paymentErrorsConfig[res.errorCode].error);
        } else {
          setPaymentError(res.error || "תקלה בכרטיס האשראי");
        }
        return;
      }

      handleCloseCardForm();
      localStorage.removeItem("orderId"); // the GC- watch id — never clear the whole storage here
      navigate(`/gift-success/${giftCheckout.giftOrderId}`);
    } catch (err) {
      console.log(err);
      setPaymentError("שגיאה בביצוע התשלום, נסו שוב");
    } finally {
      setLoading(false);
    }
  }

  const formik = useFormik({
    initialValues: {
      cardHolder: "",
      cardNumber: "",
      expiriedMonth: getCurrentMonthString(),
      expiriedYear: new Date().getFullYear(),
      cvv: "",
      id: "",
    },
    onSubmit: async (values) => {
      setLoading(true);
      setPaymentError("");
      const numberValidation = valid.number(values.cardNumber.replace(/s/g, '').replace(/_/g, '')).isPotentiallyValid && !!values.cardNumber;
      const holderValidation = valid.cardholderName(values.cardHolder).isPotentiallyValid && !!values.cardHolder;
      const expMonValidation = valid.expirationMonth(values.expiriedMonth).isPotentiallyValid && !!values.expiriedMonth;
      const expYearValidation = valid.expirationYear(values.expiriedYear.toString()).isPotentiallyValid && !!values.expiriedYear.toString();
      const cvvValidation = valid.cvv(values.cvv).isPotentiallyValid && !!values.cvv;

      if (!numberValidation || !holderValidation || !expMonValidation || !expYearValidation || !cvvValidation || !values.id) {
        setCardErr({
          cardNumberErr: !numberValidation,
          cardHolderErr: !holderValidation,
          cardExpMonErr: !expMonValidation,
          cardExpYearErr: !expYearValidation,
          cardCVVErr: !cvvValidation,
          cardIdErr: !values.id,
        })
        setLoading(false);
        return;
      }

      // Gift card: no card-check leg — one full J7 charge on the server-priced amount.
      if (giftMode) {
        await handleGiftCardPayment(values);
        return;
      }

      await payAndOrder(values, { beforeNavigate: handleCloseCardForm });
      setLoading(false);
    },
  });

  const monthArray = [...Array(12)].map((_,index) => {
    if (index >= 9) {
      return ({ value: `${index+1}`, label: `${index+1}` })
    }
    return ({ value: `0${index+1}`, label: `0${index+1}` })
  });

  const generateArrayOfYears = () => {
    const min = new Date().getFullYear()
    const max = min + 10
    const years = []

    for (var i = min - 1; i <= max; i++) {
      years.push({ value: i, label: i })
    }
    return years
  }

  const handleCloseCardForm = () => {
    cancelPending();
    setModals(state => ({
      ...state,
      addCard: { visible: false },
        selectCard: { visible: false },
    }))
    setOverlay(false);
  }

  return (
    <form className="add-new-card-form" onSubmit={formik.handleSubmit}>
      <div className="add-new-card-form__header">הקלד את פרטי הכרטיס שלך</div>
      <div className="add-new-card-form__content">
        <div className="form__description rtl" style={{fontSize: "14px"}}>
          בלנדס מאשרת את כל סוגי שיטות התשלום וכרטיסי האשראי הגדולים:
          <div className="card__examples" style={{paddingLeft: "30px", paddingTop: "15px"}}>
            <img src="/assets/file/images/card-brands.PNG" width="100%"  alt=""/>
          </div>
        </div>
        <div className="form__fields">
          <div className="form__fields--row">
            <div style={cardErr.cardNumberErr ? {border: "1px solid red", borderRadius: "6px"} : {}}>
              <Input
                name="cardNumber"
                mask=""
                type="tel"
                alwaysShowMask={false}
                onChange={(e) => {
                  let number = e.target.value.replace(/\s/g, '').replace(/_/g, '');
                  if (cardErr.cardNumberErr) {
                    setCardErr({...cardErr, cardNumberErr: false});
                  }
                  const isAmex = number.startsWith('34') || number.startsWith('37');
                  number = number.slice(0, isAmex ? 15 : 16);
                  e.target.value = isAmex
                    ? [number.slice(0, 4), number.slice(4, 10), number.slice(10, 15)].filter(Boolean).join(' ')
                    : (number.match(/.{1,4}/g) || []).join(' ');
                  formik.handleChange(e)}
                }
                placeholder=""
                value={formik.values.cardNumber}
                autoFocus={true}
                maxLength={19}
              />
            </div>
            <span>מספר כרטיס</span>
          </div>
          <div className="form__fields--row">
            <div style={cardErr.cardHolderErr ? {border: "1px solid red", borderRadius: "6px"} : {}}>
              <Input
                name="cardHolder"
                onChange={formik.handleChange}
                value={formik.values.cardHolder}
                style={{direction: "rtl"}}
              />
            </div>
            <span>שם בעל הכרטיס</span>
          </div>
          <div className="form__fields--row">
            <div className="selects" style={(cardErr.cardExpMonErr || cardErr.cardExpYearErr) ? {border: "1px solid red", borderRadius: "6px"} : {}}>
              <StyledSelect
                name="expiriedMonth"
                options={monthArray}
                placeholder={false}
                onChange={(i) => formik.setFieldValue('expiriedMonth', i.value)}
                defaultValue={monthArray.find(itm => itm.value === formik.values.expiriedMonth)}
              />
              <StyledSelect
                name="expiriedYear"
                options={generateArrayOfYears()}
                placeholder={false}
                onChange={(i) => formik.setFieldValue('expiriedYear', i.value)}
                defaultValue={generateArrayOfYears().find(itm => itm.value === formik.values.expiriedYear)}
              />
            </div>
            <span>תוקף</span>
          </div>
          <div className="form__fields--row">
            <div className="cvv_content" style={cardErr.cardCVVErr ? {border: "1px solid red", borderRadius: "6px"} : {}}>
              <Input
                name="cvv"
                onChange={formik.handleChange}
                value={formik.values.cvv}
                mask=""
                maxLength={3}
              />
            </div>
            <span>קוד בטחון (CVV)</span>
          </div>
          <div className="form__fields--row">
            <div style={cardErr.cardIdErr ? {border: "1px solid red", borderRadius: "6px"} : {}}>
              <Input
                name="id"
                type="tel"
                onChange={formik.handleChange}
                value={formik.values.id}
                mask=""
                style={{direction: "rtl"}}
              />
            </div>
            <span>ת.ז בעל הכרטיס</span>
          </div>
          {(paymentError || orderPaymentError) && (
            <div className="form__fields--row" style={{color: "red"}}>
              {paymentError || orderPaymentError}
            </div>
          )}
        </div>
      </div>
      <div className="add-new-card-form__footer">
        <button className="submit" type="submit" disabled={isLoading || isPaying}>
          {giftMode ? "אישור ותשלום" : "אישור הזמנה"}
        </button>
        <button onClick={() => handleCloseCardForm()} type="button">ביטול</button>
      </div>
      <HeartLoader isLoading={isLoading || isPaying} showText={false}/>
    </form>
  );
};

export default AddCardForm;
